import Jimp from 'jimp';
import {
  PreviewData,
  Region,
  RegionMoveDetail,
  RegionScaleDetail,
  ScreenCaptureServiceEventListenerCallback,
  ScreenCaptureServiceEventType
} from './models';
import { createWorker, PSM } from 'tesseract.js';
import { DEFAULT_REGIONS } from './config';
import { trackStart, trackStop } from './screen-capture.analytics.service';

const TIME_MS_BETWEEEN_SCANS = 200;
const LOCAL_STORAGE_REGIONS_KEY = 'SCANNER_REGIONS';

class ScreenCaptureService {
  listeners: ScreenCaptureServiceEventListenerCallback[] = [];

  regions: Region[];
  isActive = false;
  video: HTMLVideoElement;
  workers: Tesseract.Worker[] = [];
  canvas = document.createElement('canvas');
  previewCanvas = document.createElement('canvas');

  startCapturing() {
    let savedRegions;
    try {
      const savedRegionsJSON = localStorage.getItem(LOCAL_STORAGE_REGIONS_KEY);
      savedRegions = savedRegionsJSON ? JSON.parse(savedRegionsJSON) : null;
    } catch (error) {
      // fail silently
    }
    this.regions = savedRegions ?? JSON.parse(JSON.stringify(DEFAULT_REGIONS));
    this.isActive = true;
    navigator.mediaDevices
      .getDisplayMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { max: 30 }
        },
        audio: false
      })
      .then(async (mediaStream) => {
        await this.setupRecognitionWorkers();

        this.video = document.createElement('video');
        this.video.srcObject = mediaStream;
        this.video.addEventListener('loadedmetadata', () => {
          this.startScanning();
          trackStart(this.video);
        });
        const track = mediaStream.getVideoTracks()[0];
        track.addEventListener('ended', () => {
          this.stopCapturing();
        });
        this.video.play();
      })
      .catch(() => {
        // Fail silently. It's likely that permission to capture screen was denied by the User.
      });
  }

  stopCapturing() {
    trackStop();
    this.stopScanning();
    const stream: MediaStream = this.video.srcObject as MediaStream;
    if (stream) {
      const tracks = stream.getTracks();
      tracks.forEach((track) => {
        track.stop();
      });
      this.video.srcObject = null;
    }
  }

  async setupRecognitionWorkers() {
    this.sendEventToListeners('INITIALIZING');
    if (this.workers.length === 0) {
      try {
        const firstWorker = await this.setupWorker();
        this.workers.push(firstWorker);

        const remainingWorkersCount = this.regions.length * 6 - 1;
        const remainingWorkers = await Promise.all(
          Array(remainingWorkersCount)
            .fill(null)
            .map(() => this.setupWorker())
        );

        this.workers.push(...remainingWorkers);
      } catch (err) {
        // It can happen that the data for Tesseract text recognition is corrupted.
        // In this case we clear the database and reinitialize the workers.
        await this.clearTesseractDB();
        await this.setupRecognitionWorkers();
      }
    }
  }

  private async setupWorker(): Promise<Tesseract.Worker> {
    let worker;
    try {
      worker = createWorker();

      await worker.load();
      await worker.loadLanguage('eng');
      await worker.initialize('eng');
      await worker.setParameters({
        // eslint-disable-next-line @typescript-eslint/camelcase
        tessedit_pageseg_mode: PSM.SINGLE_CHAR
      });
      return worker;
    } catch (err) {
      if (worker) {
        await worker.terminate();
      }
      throw err;
    }
  }

  private async clearTesseractDB() {
    await new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase('keyval-store');
      request.onsuccess = () => {
        resolve(null);
      };
      request.onerror = () => {
        reject();
      };
      request.onblocked = () => {
        reject();
      };
      request.onupgradeneeded = () => {
        reject();
      };
    }).catch(() => {
      return this.clearTesseractDB();
    });
  }

  private startScanning() {
    this.sendEventToListeners('STARTED');
    this.scanFrameRecurrent();
  }

  private scanFrameRecurrent() {
    if (this.isActive) {
      window.requestAnimationFrame(async () => {
        await this.scanFrame();
        setTimeout(() => {
          this.scanFrameRecurrent();
        }, TIME_MS_BETWEEEN_SCANS);
      });
    }
  }

  private async stopScanning() {
    this.sendEventToListeners('STOPPED');
    this.isActive = false;
    this.cleanupWorkers();
  }

  private scanFrame() {
    const allGeneScans = this.getSaplingGenesScans();
    const promises: Promise<string | null>[] = [];
    allGeneScans.forEach((geneScans, regionIndex) => {
      geneScans.forEach((geneImgData, index) => {
        promises.push(this.getRecognizedGene(geneImgData, regionIndex * 6 + index));
      });
    });

    return Promise.all(promises).then((results) => {
      const regionResults: (string | null)[][] = [];
      this.regions.forEach(() => {
        regionResults.push(results.splice(0, 6));
      });

      regionResults.forEach((regionGenes) => {
        const saplingGenesString = regionGenes.map((gene) => (gene ? gene : '')).join('');
        if (saplingGenesString.match(/^[GHYWX]{6}$/g)) {
          this.sendEventToListeners('SAPLING-FOUND', saplingGenesString);
        }
      });
    });
  }

  private getRecognizedGene(imgData: ImageData, workerIndex: number): Promise<string | null> {
    const promise = new Promise<string | null>((resolve) => {
      new Jimp(
        { data: Buffer.from(imgData.data.buffer), width: imgData.width, height: imgData.height },
        (err, image) => {
          if (err) {
            resolve(null);
          }

          image
            .greyscale()
            .invert()
            .normalize()
            .scale(2)
            .convolute([
              [0, -0.5, 0],
              [-0.5, 4, -0.5],
              [0, -0.5, 0]
            ])
            .contrast(0.5)
            .getBase64('image/png', async (err, data) => {
              // TODO: figure out a way to stop console logs
              let {
                data: { text }
              } = await this.workers[workerIndex].recognize(data);

              text = text.replace(/\s/g, '').toUpperCase();

              if (text.match(/^[GHYWX]{1}$/g)) {
                resolve(text);
              } else {
                resolve(null);
              }
            });
        }
      );
    });
    return promise;
  }

  private getRegionComputedProperties(region: Region) {
    const regionTopLeftCornerXPx = Math.round(this.video.videoWidth * region.TOP_LEFT_X);
    const regionTopLeftCornerYPx = Math.round(this.video.videoHeight * region.TOP_LEFT_Y);
    const regionWidthPx = Math.round(this.video.videoWidth * region.WIDTH);
    const regionHeight = region.WIDTH * region.HEIGHT_TO_WIDTH_RATIO;
    const regionHeightPx = Math.ceil(this.video.videoHeight * regionHeight);
    const geneWidthPx = Math.round(regionWidthPx * region.GENE_WIDTH_TO_WIDTH_RATIO);

    return {
      regionTopLeftCornerXPx,
      regionTopLeftCornerYPx,
      regionWidthPx,
      regionHeight,
      regionHeightPx,
      geneWidthPx
    };
  }

  private getSaplingGenesScans(): ImageData[][] {
    const allGeneScans: ImageData[][] = [];
    const canvasCtx = this.canvas.getContext('2d', {
      willReadFrequently: true
    })!;

    this.regions.forEach((region, regionIndex) => {
      const geneScans = [];

      const {
        regionTopLeftCornerXPx,
        regionTopLeftCornerYPx,
        regionWidthPx,
        regionHeightPx,
        geneWidthPx
      } = this.getRegionComputedProperties(region);

      const geneCount = 6;
      const gapCount = geneCount - 1;
      const totalGenesWidth = geneWidthPx * geneCount;
      const gapPx = (regionWidthPx - totalGenesWidth) / gapCount;

      this.canvas.height = regionHeightPx;
      this.canvas.width = regionWidthPx;

      canvasCtx.drawImage(
        this.video,
        regionTopLeftCornerXPx,
        regionTopLeftCornerYPx,
        regionWidthPx,
        regionHeightPx,
        0,
        0,
        regionWidthPx,
        regionHeightPx
      );

      for (let genePosition = 0; genePosition < geneCount; genePosition++) {
        const saplingGenesXPxStart = genePosition * (geneWidthPx + gapPx);

        const imgData = canvasCtx.getImageData(saplingGenesXPxStart, 0, geneWidthPx, regionHeightPx);
        geneScans.push(imgData);
      }
      this.sendPreview(regionIndex, canvasCtx);

      allGeneScans.push(geneScans);
    });
    return allGeneScans;
  }

  sendPreview(regionIndex: number, recyclableCanvasCtx?: CanvasRenderingContext2D) {
    const region = this.regions[regionIndex];
    const {
      regionTopLeftCornerXPx,
      regionTopLeftCornerYPx,
      regionWidthPx,
      regionHeightPx,
      geneWidthPx
    } = this.getRegionComputedProperties(region);

    let canvasCtx = recyclableCanvasCtx;
    if (!canvasCtx) {
      this.previewCanvas.width = regionWidthPx;
      this.previewCanvas.height = regionHeightPx;
      canvasCtx = this.previewCanvas.getContext('2d', {
        willReadFrequently: true
      })!;

      canvasCtx.drawImage(
        this.video,
        regionTopLeftCornerXPx,
        regionTopLeftCornerYPx,
        regionWidthPx,
        regionHeightPx,
        0,
        0,
        regionWidthPx,
        regionHeightPx
      );
    }

    const geneCount = 6;
    const totalGenesWidth = geneWidthPx * geneCount;
    const gapCount = geneCount - 1;
    const gapPx = (regionWidthPx - totalGenesWidth) / gapCount;

    canvasCtx.fillStyle = 'black';
    canvasCtx.globalAlpha = 0.35;

    for (let i = 0; i < gapCount; i++) {
      const x = geneWidthPx + i * (geneWidthPx + gapPx);
      const width = gapPx;
      canvasCtx.fillRect(x, 0, width, regionHeightPx);
    }
    const imgData = canvasCtx.getImageData(0, 0, regionWidthPx, regionHeightPx);
    this.sendEventToListeners('PREVIEW', { imgData, regionIndex });
  }

  sendEventToListeners(eventName: ScreenCaptureServiceEventType, data?: string | PreviewData) {
    this.listeners.forEach((listenerCallback) => {
      listenerCallback(eventName, data);
    });
  }

  addEventListener(callback: ScreenCaptureServiceEventListenerCallback) {
    this.listeners.push(callback);
  }

  moveRegion(regionIndex: number, detail: RegionMoveDetail) {
    const isVertical = detail.direction === 'up' || detail.direction === 'down';
    const propertyToUpdate = isVertical ? 'TOP_LEFT_Y' : 'TOP_LEFT_X';
    let step = isVertical ? 1 / this.video.videoHeight : 1 / this.video.videoWidth;
    if (detail.amplified) {
      step *= 3;
    }
    const factor = detail.direction === 'down' || detail.direction === 'right' ? 1 : -1;
    const region = this.regions[regionIndex];
    let newValue = region[propertyToUpdate] + factor * step;

    if (isVertical) {
      newValue = Math.max(0, Math.min(newValue, 1 - region.HEIGHT_TO_WIDTH_RATIO * region.WIDTH));
    } else {
      newValue = Math.max(0, Math.min(newValue, 1 - region.WIDTH));
    }

    this.regions[regionIndex] = {
      ...region,
      [propertyToUpdate]: newValue
    };

    this.sendPreview(regionIndex);
  }

  scaleRegion(regionIndex: number, detail: RegionScaleDetail) {
    let step = 1 / this.video.videoWidth;
    if (detail.amplified) {
      step *= 3;
    }
    const factor = detail.direction === 'up' ? 1 : -1;
    const region = this.regions[regionIndex];
    let newWidth = region.WIDTH + factor * step;
    newWidth = Math.max(step, Math.min(newWidth, 1));

    const isWidthWitinBounds = region.TOP_LEFT_X + newWidth < 1;
    const isHeightWitinBounds = region.HEIGHT_TO_WIDTH_RATIO * newWidth < 1;

    if (isWidthWitinBounds && isHeightWitinBounds) {
      this.regions[regionIndex] = {
        ...region,
        WIDTH: newWidth
      };
    }

    this.sendPreview(regionIndex);
  }

  saveRegions() {
    const regionsJSON = JSON.stringify(this.regions);
    localStorage.setItem(LOCAL_STORAGE_REGIONS_KEY, regionsJSON);
    gtag('event', 'save_regions', {
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      videoResolution: `${this.video.videoWidth}x${this.video.videoHeight}`,
      regionsJSON
    });
  }

  resetRegions() {
    localStorage.removeItem(LOCAL_STORAGE_REGIONS_KEY);
    this.regions = JSON.parse(JSON.stringify(DEFAULT_REGIONS));
    this.regions.forEach((_, regionIndex) => {
      this.sendPreview(regionIndex);
    });
    gtag('event', 'reset_regions');
  }

  private cleanupWorkers() {
    this.workers.forEach((worker) => {
      worker.terminate();
    });
    this.workers = [];
  }
}

export default new ScreenCaptureService();
